import { FilterCreatorsDto } from '@/app/common/general-types/filters.dto';
import { ApplicationStatusEnum, BriefApplicationResponseDto } from '@/app/common/general-types/briefs.dto';
import { assignmentStageIndex } from '@/app/pages/manage-brief-tabs/tabs/assignment/assignment-view.helpers';

/** Terminal declined / removed rows should never appear on the assignments list. */
export function isHiddenFromAssignmentList(
  applicant: Pick<BriefApplicationResponseDto, 'applicationStatus'>,
): boolean {
  return (
    applicant.applicationStatus === ApplicationStatusEnum.ApplicationDeclined ||
    applicant.applicationStatus === ApplicationStatusEnum.InviteDeclined ||
    applicant.applicationStatus === ApplicationStatusEnum.InviteRemoved ||
    applicant.applicationStatus === ApplicationStatusEnum.HiredRemoved
  );
}

export function excludeHiddenAssignmentApplicants(
  applicants: BriefApplicationResponseDto[],
): BriefApplicationResponseDto[] {
  return applicants.filter((applicant) => !isHiddenFromAssignmentList(applicant));
}

/**
 * Merge assignment profile filters with keyword search + status tab filters
 * into a FilterCreatorsDto for getApplicants.
 */
export function mergeApplicantFilters(
  profile: FilterCreatorsDto,
  magic: FilterCreatorsDto,
  sort?: { sortField?: string; sortOrder?: string },
): FilterCreatorsDto {
  const merged: FilterCreatorsDto = { ...profile };

  if (magic.searchTerm) {
    merged.searchTerm = magic.searchTerm;
  }
  if (magic.applicationStatus !== undefined) {
    merged.applicationStatus = magic.applicationStatus;
  }
  if (sort?.sortField) {
    merged.sortField = sort.sortField;
  }
  if (sort?.sortOrder) {
    merged.sortOrder = sort.sortOrder;
  }

  return merged;
}

function toSortableMs(value: Date | string | null | undefined): number | null {
  if (value == null) {
    return null;
  }
  const ms = value instanceof Date ? value.getTime() : Date.parse(String(value));
  return Number.isFinite(ms) ? ms : null;
}

function applicantSortValue(
  applicant: BriefApplicationResponseDto,
  sortField: string,
): string | number | null {
  if (sortField === 'autoDeclineAt' || sortField === 'application.autoDeclineAt') {
    return toSortableMs(applicant.autoDeclineAt);
  }
  if (sortField === 'applicationStatusModifiedDate') {
    return toSortableMs(applicant.applicationStatusModifiedDate);
  }
  if (sortField === 'creator.firstName' || sortField === 'creator.name') {
    const first = (applicant.creator?.firstName ?? '').trim().toLowerCase();
    const last = (applicant.creator?.lastName ?? '').trim().toLowerCase();
    return `${first} ${last}`.trim() || null;
  }
  if (sortField === 'creator.lastName') {
    return (applicant.creator?.lastName ?? '').trim().toLowerCase() || null;
  }
  if (sortField === 'needsYou') {
    // Needs-you rows above the rest, each group newest first — same as the API.
    const modified = toSortableMs(applicant.applicationStatusModifiedDate) ?? 0;
    return (applicant.needsYou ? NEEDS_YOU_BOOST_MS : 0) + modified;
  }
  if (sortField === 'stageRank') {
    return assignmentStageIndex(applicant.applicationStatus);
  }
  if (sortField === 'followers') {
    const creator = applicant.creator;
    return (creator?.tikTokFollowers || 0) + (creator?.instaFollowers || 0) + (creator?.youtubeSubscribers || 0);
  }
  return null;
}

/** Larger than any epoch-ms timestamp, so needs-you rows always sort first. */
const NEEDS_YOU_BOOST_MS = 1e15;

/**
 * Client-side sort used after profile/screening filters shrink a large page.
 * Nulls always sort last so Applied expiry dates stay at the top.
 */
export function sortApplicantsClientSide(
  applicants: BriefApplicationResponseDto[],
  sortField?: string,
  sortOrder?: string,
): BriefApplicationResponseDto[] {
  if (!sortField || !sortOrder) {
    return applicants;
  }
  const dir = sortOrder === 'ASC' ? 1 : -1;
  return [...applicants].sort((a, b) => {
    const av = applicantSortValue(a, sortField);
    const bv = applicantSortValue(b, sortField);
    if (av == null && bv == null) {
      return 0;
    }
    if (av == null) {
      return 1;
    }
    if (bv == null) {
      return -1;
    }
    if (typeof av === 'string' && typeof bv === 'string') {
      return av.localeCompare(bv) * dir;
    }
    return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
  });
}
